'use client';

import { useEffect, useState } from 'react';
import Link from 'next/link';
import { usePathname } from 'next/navigation';
import { X } from 'lucide-react';
import { useAuthContext } from '@/components/providers/AuthProvider';
import { createClient } from '@/lib/supabase/browser';

const TOTAL_STEPS = 5;
const SKIP_REMINDER_KEY = 'indiestry-onboarding-skip-reminder-dismissed';
const SKIP_REMINDER_INTERVAL_MS = 7 * 24 * 60 * 60 * 1000; // 1 week

export default function OnboardingBanner() {
  const { user, loading } = useAuthContext();
  const pathname = usePathname();
  const [profile, setProfile] = useState<{
    onboarding_completed_at: string | null;
    onboarding_step: number | null;
  } | null>(null);
  const [hiddenThisSession, setHiddenThisSession] = useState(false);
  const [skipDismissedRecently, setSkipDismissedRecently] = useState(false);

  useEffect(() => {
    if (loading || !user?.id) return;
    let cancelled = false;
    (async () => {
      const supabase = createClient();
      const { data } = await supabase
        .from('profiles')
        .select('onboarding_completed_at, onboarding_step')
        .eq('id', user.id)
        .maybeSingle();
      if (cancelled) return;
      setProfile({
        onboarding_completed_at: data?.onboarding_completed_at ?? null,
        onboarding_step: data?.onboarding_step ?? 0,
      });
    })();
    return () => {
      cancelled = true;
    };
  }, [user?.id, loading, pathname]);

  useEffect(() => {
    try {
      const last = localStorage.getItem(SKIP_REMINDER_KEY);
      if (last) {
        const ts = parseInt(last, 10);
        if (Number.isFinite(ts) && Date.now() - ts < SKIP_REMINDER_INTERVAL_MS) {
          setSkipDismissedRecently(true);
        }
      }
    } catch {
      // ignore — best-effort
    }
  }, []);

  // Hide on the onboarding page itself or any auth screen, when the user
  // has explicitly finished, when they have an onboarding_step at or past
  // the final step (defensive — covers accounts that completed onboarding
  // before the onboarding_completed_at timestamp column existed and so
  // never got the timestamp written), or when this session has dismissed.
  const stepValue = profile?.onboarding_step ?? 0;
  if (
    !user ||
    !profile ||
    profile.onboarding_completed_at ||
    stepValue >= TOTAL_STEPS ||
    hiddenThisSession ||
    pathname?.startsWith('/onboarding') ||
    pathname?.startsWith('/auth') ||
    pathname?.startsWith('/login') ||
    pathname?.startsWith('/signup') ||
    pathname?.startsWith('/create-account')
  ) {
    return null;
  }

  const skippedAll = profile.onboarding_step === -1;

  // Slim weekly nag for users who explicitly opted out.
  if (skippedAll) {
    if (skipDismissedRecently) return null;
    return (
      <div className="bg-surface-2 border-b border-border-subtle px-4 py-2">
        <div className="max-w-7xl mx-auto flex items-center justify-between gap-3 text-meta">
          <span className="text-text-secondary">
            Profile incomplete. Add a demo reel to be more discoverable.{' '}
            <Link
              href="/onboarding?step=1"
              className="text-accent hover:underline font-medium"
            >
              Complete now
            </Link>
          </span>
          <button
            type="button"
            onClick={() => {
              try {
                localStorage.setItem(SKIP_REMINDER_KEY, String(Date.now()));
              } catch {
                /* ignore */
              }
              setSkipDismissedRecently(true);
            }}
            aria-label="Dismiss"
            className="text-text-tertiary hover:text-text-secondary transition-colors flex-shrink-0"
          >
            <X className="w-3.5 h-3.5" strokeWidth={2} />
          </button>
        </div>
      </div>
    );
  }

  const stepsDone = Math.max(0, Math.min(TOTAL_STEPS, profile.onboarding_step ?? 0));
  const resumeStep = stepsDone > 0 ? stepsDone : 1;

  return (
    <div className="bg-accent-soft border-b border-accent/30 px-4 py-2">
      <div className="max-w-7xl mx-auto flex items-center justify-between gap-3">
        <span className="text-meta text-text-primary">
          Complete your profile to get discovered.{' '}
          <span className="text-text-secondary tabular-nums">
            ({stepsDone}/{TOTAL_STEPS} done)
          </span>
        </span>
        <div className="flex items-center gap-2 flex-shrink-0">
          <Link
            href={`/onboarding?step=${resumeStep}`}
            className="inline-flex h-7 items-center px-2.5 rounded-sm bg-accent text-text-on-accent text-xs font-medium hover:bg-accent-hover transition-colors"
          >
            Resume
          </Link>
          <button
            type="button"
            onClick={() => setHiddenThisSession(true)}
            aria-label="Dismiss"
            className="text-text-tertiary hover:text-text-secondary transition-colors p-1"
          >
            <X className="w-3.5 h-3.5" strokeWidth={2} />
          </button>
        </div>
      </div>
    </div>
  );
}
